import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import MuxPlayer from "@mux/mux-player-react";
import { Film, Plus, Trash2, ExternalLink, X, Search } from "lucide-react";

const VARIANT_TYPES = [
  { key: "part1", label: "Part 1" },
  { key: "part1_trailer", label: "Part 1 Trailer" },
  { key: "part2", label: "Part 2" },
  { key: "part2_trailer", label: "Part 2 Trailer" },
  { key: "part3", label: "Part 3" },
  { key: "part3_trailer", label: "Part 3 Trailer" },
  { key: "onlyfans_cut", label: "OnlyFans Cut" },
  { key: "onlyfans_trailer", label: "OnlyFans Trailer" },
];

export default function VideoVariantsManager({ video, onClose, onUpdate }) {
  const [variants, setVariants] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedVariantType, setSelectedVariantType] = useState(null);
  const [addMode, setAddMode] = useState(null); // 'manual' | 'library'
  const [manualPlaybackId, setManualPlaybackId] = useState("");
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadVariants = useCallback(async () => {
    try {
      const res = await api.get(`/api/mux/variants/${video.id}`);
      setVariants(res.data.variants || {});
    } catch (error) {
      console.error("Error loading variants:", error);
    } finally {
      setLoading(false);
    }
  }, [video.id]);

  useEffect(() => {
    loadVariants();
  }, [loadVariants]);

  const loadLibrary = async () => {
    setLoadingLibrary(true);
    try {
      const res = await api.get("/api/mux/library/assets", { params: { limit: 100 } });
      setLibraryAssets(res.data.assets || []);
    } catch (error) {
      toast.error("Failed to load Mux library");
      console.error(error);
    } finally {
      setLoadingLibrary(false);
    }
  };

  const addVariant = (variantType) => {
    setSelectedVariantType(variantType);
    setShowAddModal(true);
    setAddMode(null);
    setManualPlaybackId("");
  };

  const saveManualVariant = async () => {
    if (!manualPlaybackId.trim()) {
      toast.error("Please enter a Mux playback ID");
      return;
    }
    if (!video?.id) {
      toast.error("This video has no ID yet — save the video first, then add variants.");
      return;
    }

    try {
      await api.post(`/api/mux/variants/${video.id}/${selectedVariantType}`, {
        playback_id: manualPlaybackId.trim()
      });
      toast.success("Variant added successfully!");
      setShowAddModal(false);
      loadVariants();
      onUpdate?.();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || "Unknown error";
      toast.error(`Add failed: ${detail}`);
      console.error("Add variant (manual) failed:", error?.response?.status, detail);
    }
  };

  const saveLibraryVariant = async (assetId) => {
    if (!video?.id) {
      toast.error("This video has no ID yet — save the video first, then add variants.");
      return;
    }
    try {
      await api.post(`/api/mux/variants/${video.id}/${selectedVariantType}`, {
        asset_id: assetId
      });
      toast.success("Variant added successfully!");
      setShowAddModal(false);
      loadVariants();
      onUpdate?.();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || "Unknown error";
      toast.error(`Add failed: ${detail}`);
      console.error("Add variant (library) failed:", error?.response?.status, detail);
    }
  };

  const deleteVariant = async (variantType) => {
    if (!window.confirm(`Delete ${VARIANT_TYPES.find(v => v.key === variantType)?.label}?`)) {
      return;
    }

    try {
      await api.delete(`/api/mux/variants/${video.id}/${variantType}`);
      toast.success("Variant deleted");
      loadVariants();
      onUpdate?.();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || "Unknown error";
      toast.error(`Delete failed: ${detail}`);
      console.error("Delete variant failed:", error?.response?.status, detail);
    }
  };

  const filteredAssets = libraryAssets.filter(asset => 
    asset.playback_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="text-white">Loading variants...</div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-zinc-900 border-2 border-pink-500/40 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="p-6 border-b border-pink-500/20 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Film className="text-pink-400" size={24} />
                Video Variants Manager
              </h2>
              <p className="text-gray-400 text-sm mt-1">{video.title}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X size={24} />
            </button>
          </div>

          {/* Variants Grid */}
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {VARIANT_TYPES.map((variantType) => {
              const variant = variants[variantType.key];
              const hasVariant = !!variant;

              return (
                <div key={variantType.key} className="border border-zinc-700 rounded-xl p-4 bg-zinc-800/50">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-white">{variantType.label}</h3>
                    {hasVariant ? (
                      <button
                        onClick={() => deleteVariant(variantType.key)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    ) : (
                      <button
                        onClick={() => addVariant(variantType.key)}
                        className="text-pink-400 hover:text-pink-300 transition-colors"
                      >
                        <Plus size={16} />
                      </button>
                    )}
                  </div>

                  {hasVariant ? (
                    <div>
                      <div className="bg-black rounded-lg overflow-hidden mb-2">
                        <MuxPlayer
                          playbackId={variant.mux_playback_id}
                          style={{ width: "100%", aspectRatio: "16/9" }}
                          streamType="on-demand"
                          autoPlay={false}
                          muted
                        />
                      </div>
                      <div className="text-xs text-gray-400 space-y-1">
                        <div>Status: <span className="text-cyan-400">{variant.status}</span></div>
                        {variant.duration && <div>Duration: {Math.floor(variant.duration / 60)}:{String(Math.floor(variant.duration % 60)).padStart(2, '0')}</div>}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-gray-500 truncate">{variant.mux_playback_id}</span>
                          <a 
                            href={`https://stream.mux.com/${variant.mux_playback_id}.m3u8`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-pink-400 hover:text-pink-300"
                          >
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 border-2 border-dashed border-zinc-700 rounded-lg">
                      <Film size={24} className="mx-auto mb-2 opacity-50" />
                      <div className="text-sm">No variant added</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-pink-500/20 flex justify-end sticky bottom-0 bg-zinc-900">
            <button onClick={onClose} className="px-6 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors">
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Add Variant Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-zinc-900 border-2 border-cyan-500/40 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-cyan-500/20">
              <h3 className="text-xl font-bold text-white">
                Add {VARIANT_TYPES.find(v => v.key === selectedVariantType)?.label}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              {!addMode ? (
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setAddMode('manual')}
                    className="p-6 border-2 border-cyan-500/30 rounded-xl hover:border-cyan-500 hover:bg-cyan-500/10 transition-all"
                  >
                    <div className="text-cyan-400 text-lg font-semibold mb-2">Enter Mux ID</div>
                    <div className="text-gray-400 text-sm">Manually enter a Mux playback ID</div>
                  </button>
                  <button
                    onClick={() => {
                      setAddMode('library');
                      loadLibrary();
                    }}
                    className="p-6 border-2 border-pink-500/30 rounded-xl hover:border-pink-500 hover:bg-pink-500/10 transition-all"
                  >
                    <div className="text-pink-400 text-lg font-semibold mb-2">Pick from Library</div>
                    <div className="text-gray-400 text-sm">Choose from all Mux videos</div>
                  </button>
                </div>
              ) : addMode === 'manual' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Mux Playback ID
                  </label>
                  <input
                    type="text"
                    value={manualPlaybackId}
                    onChange={(e) => setManualPlaybackId(e.target.value)}
                    placeholder="e.g., abc123xyz456..."
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                  />
                  <div className="flex gap-3 mt-4">
                    <button onClick={() => setAddMode(null)} className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg">
                      Back
                    </button>
                    <button onClick={saveManualVariant} className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-semibold">
                      Add Variant
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by playback ID or asset ID..."
                        className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-pink-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  {loadingLibrary ? (
                    <div className="text-center py-8 text-gray-400">Loading library...</div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                      {filteredAssets.map((asset) => (
                        <button
                          key={asset.id}
                          onClick={() => saveLibraryVariant(asset.id)}
                          className="text-left border border-zinc-700 rounded-lg p-3 hover:border-pink-500 hover:bg-pink-500/10 transition-all"
                        >
                          {asset.playback_id ? (
                            <div className="bg-black rounded mb-2">
                              <MuxPlayer
                                playbackId={asset.playback_id}
                                style={{ width: "100%", aspectRatio: "16/9" }}
                                streamType="on-demand"
                                autoPlay={false}
                                muted
                              />
                            </div>
                          ) : (
                            <div className="bg-zinc-800 rounded mb-2 aspect-video flex items-center justify-center">
                              <Film size={24} className="text-gray-600" />
                            </div>
                          )}
                          <div className="text-xs space-y-1">
                            <div className="text-white font-medium truncate">{asset.playback_id || asset.id}</div>
                            <div className="text-gray-400">{asset.status}</div>
                            {asset.duration && <div className="text-gray-500">{Math.floor(asset.duration / 60)}:{String(Math.floor(asset.duration % 60)).padStart(2, '0')}</div>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  <button onClick={() => setAddMode(null)} className="w-full mt-4 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg">
                    Back
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
